
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/utils/notification';
import { Send, Plus, Edit, Trash2, Users, User } from 'lucide-react';
import { format } from 'date-fns';

interface SystemMessage {
  id: string;
  title: string;
  body: string;
  target_type: 'all' | 'all_clients' | 'all_practitioners' | 'specific_user';
  target_user_email?: string;
  created_at: string;
  is_active: boolean;
  expires_at?: string;
}

const AdminSupportTickets: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<SystemMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState<'all' | 'all_clients' | 'all_practitioners' | 'specific_user'>('all_clients');
  const [targetEmail, setTargetEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('system_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching messages:', error);
        notify.error('Error', 'Failed to fetch message list');
        return;
      }

      setMessages((data || []) as SystemMessage[]);
    } catch (err) {
      console.error('Unexpected error:', err);
      notify.error('Error', 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !body.trim()) {
      notify.error('Error', 'Please fill in title and content');
      return;
    }

    if (targetType === 'specific_user' && !targetEmail.trim()) {
      notify.error('Error', 'Please fill in target user email');
      return;
    }

    setIsSubmitting(true);
    try {
      let resolvedUserId: string | null = null;
      if (targetType === 'specific_user' && targetEmail.trim()) {
        try {
          const { data: emailCheck }: any = await supabase.rpc('check_email_uniqueness_across_roles', {
            p_email: targetEmail.trim(),
            p_user_type: 'client'
          });
          if (emailCheck && emailCheck.user_id) {
            resolvedUserId = emailCheck.user_id;
          }
        } catch (e) {
          console.warn('Failed to resolve user id by email for system message:', e);
        }
      }

      const messageData: any = {
        title: title.trim(),
        body: body.trim(),
        target_type: targetType,
        target_user_email: targetType === 'specific_user' ? targetEmail.trim() : null,
        target_user_id: targetType === 'specific_user' ? resolvedUserId : null,
        created_by: user?.id,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        is_active: true
      };

      const { error } = await supabase
        .from('system_messages')
        .insert([messageData]);

      if (error) {
        console.error('Error creating message:', error);
        notify.error('Error', 'Failed to send message');
        return;
      }

      notify.success('Success', 'Message sent successfully');
      
      // Reset form
      setTitle('');
      setBody('');
      setTargetType('all_clients');
      setTargetEmail('');
      setExpiresAt('');
      
      // Refresh messages list
      fetchMessages();
    } catch (err) {
      console.error('Unexpected error:', err);
      notify.error('Error', 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;

    try {
      const { error } = await supabase
        .from('system_messages')
        .delete()
        .eq('id', messageId);

      if (error) {
        console.error('Error deleting message:', error);
        notify.error('Error', 'Failed to delete message');
        return;
      }

      notify.success('Success', 'Message deleted');
      fetchMessages();
    } catch (err) {
      console.error('Unexpected error:', err);
      notify.error('Error', 'An unexpected error occurred');
    }
  };

  const getTargetTypeDisplay = (type: string, email?: string) => {
    switch (type) {
      case 'all':
        return <Badge variant="default"><Users className="w-3 h-3 mr-1" />All Users</Badge>;
      case 'all_clients':
        return <Badge variant="secondary"><Users className="w-3 h-3 mr-1" />All Clients</Badge>;
      case 'all_practitioners':
        return <Badge variant="outline"><Users className="w-3 h-3 mr-1" />All Practitioners</Badge>;
      case 'specific_user':
        return <Badge variant="default"><User className="w-3 h-3 mr-1" />{email}</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">System Messages</h2>
      
      {/* Message Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>Send New Message</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter message title"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="target-type">Target Audience *</Label>
                <Select value={targetType} onValueChange={(value: any) => setTargetType(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="all_clients">All Clients</SelectItem>
                    <SelectItem value="all_practitioners">All Practitioners</SelectItem>
                    <SelectItem value="specific_user">Specific User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {targetType === 'specific_user' && (
              <div className="space-y-2">
                <Label htmlFor="target-email">User Email *</Label>
                <Input
                  id="target-email"
                  type="email"
                  value={targetEmail}
                  onChange={(e) => setTargetEmail(e.target.value)}
                  placeholder="Enter user email address"
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="body">Message Content *</Label>
              <Textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter message content"
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires-at">Expiration Time (Optional)</Label>
              <Input
                id="expires-at"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full md:w-auto"
            >
              <Send className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Messages List */}
      <Card>
        <CardHeader>
          <CardTitle>Sent Messages</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No sent messages</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Send Time</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((message) => (
                  <TableRow key={message.id}>
                    <TableCell className="font-medium">
                      <div>
                        <p className="font-semibold">{message.title}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-xs">
                          {message.body}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getTargetTypeDisplay(message.target_type, message.target_user_email)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{format(new Date(message.created_at), 'yyyy-MM-dd')}</p>
                        <p className="text-muted-foreground">
                          {format(new Date(message.created_at), 'HH:mm')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={message.is_active ? "default" : "secondary"}>
                        {message.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {message.expires_at && new Date(message.expires_at) < new Date() && (
                        <Badge variant="destructive" className="ml-1">Expired</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(message.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSupportTickets;
